import { useMemo } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import type { AvailabilityMode } from '../../components/availability/availability-form/types';
import { type CalendarEntityType, useCalendarStore } from '../../stores/calendar.store';
import { transformScheduleDefinitionGroupsToSchedules } from './calendar-schedule-definitions';

interface LocationEntityIds {
  locationId: string;
  providerIds?: string[];
  operatoryIds?: string[];
}

const getAvailabilityModeForCalendarEntityType = (calendarEntityType: CalendarEntityType): AvailabilityMode => {
  return calendarEntityType === 'operatory' ? 'OPERATORY' : 'PROVIDER';
};

export const useGetCalendarScheduleV3Data = (locationWiseEntityIds: LocationEntityIds[]) => {
  const { isScheduleDefinitionsEnabled, isLoading } = SchedulerV3.Helpers.useGetScheduleDefinitionsFeatureFlagDetails();
  const { calendarDate, calendarEntityType } = useCalendarStore(['calendarDate', 'calendarEntityType']);
  const shouldFetchEntityScheduleData = !isLoading && isScheduleDefinitionsEnabled === false;
  const shouldFetchScheduleDefinitions = !isLoading && isScheduleDefinitionsEnabled === true;
  const locationIds = useMemo(() => locationWiseEntityIds.map((item) => item.locationId), [locationWiseEntityIds]);
  const selectedAvailabilityMode = getAvailabilityModeForCalendarEntityType(calendarEntityType);

  const listLocationSettingsQuery = SchedulerV3.Queries.useListLocationSettingsQuery({
    request: { locationIds },
    options: { enabled: locationIds.length > 0 && shouldFetchScheduleDefinitions },
  });

  const availabilityModeByLocationId = useMemo(
    () =>
      locationIds.reduce<Record<string, AvailabilityMode>>((accumulator, locationId) => {
        accumulator[locationId] = (listLocationSettingsQuery.data?.locationsSettings?.[locationId]?.availabilityLens ||
          'PROVIDER') as AvailabilityMode;
        return accumulator;
      }, {}),
    [listLocationSettingsQuery.data?.locationsSettings, locationIds]
  );

  const shouldShowSchedulesByLocationId = useMemo(
    () =>
      locationIds.reduce<Record<string, boolean>>((accumulator, locationId) => {
        accumulator[locationId] =
          !listLocationSettingsQuery.isLoading && availabilityModeByLocationId[locationId] === selectedAvailabilityMode;
        return accumulator;
      }, {}),
    [availabilityModeByLocationId, listLocationSettingsQuery.isLoading, locationIds, selectedAvailabilityMode]
  );

  const scheduleDefinitionQueries = SchedulerV3.Queries.useListScheduleDefinitionsQueries(
    locationIds.map((locationId) => ({
      request: { locationId, availabilityLens: availabilityModeByLocationId[locationId] || 'PROVIDER' },
      options: {
        enabled: !!locationId && shouldFetchScheduleDefinitions && shouldShowSchedulesByLocationId[locationId],
      },
    }))
  );

  const scheduleExceptionQueries = SchedulerV3.Queries.useListScheduleExceptionsQueries(
    locationWiseEntityIds.map(({ locationId }) => {
      return {
        request: {
          locationId,
        },
        options: {
          enabled: !!locationId && shouldFetchScheduleDefinitions && shouldShowSchedulesByLocationId[locationId],
        },
      };
    })
  );

  const officeScheduleQueries = SchedulerV3.Queries.useListSchedulesQueries(
    locationIds.map((locationId) => ({
      request: { locationId, ids: [locationId] },
      options: { enabled: !!locationId && shouldFetchEntityScheduleData },
    }))
  );

  const providerScheduleQueries = SchedulerV3.Queries.useListSchedulesQueries(
    locationWiseEntityIds.map(({ locationId, providerIds }) => ({
      request: { locationId, ids: providerIds ?? [] },
      options: {
        enabled: !!providerIds?.length && shouldFetchEntityScheduleData,
      },
    }))
  );

  const officeSchedulesQuery = useMemo(
    () => ({
      data: officeScheduleQueries.map((query) => query.data?.schedules || []).flat(),
      isLoading: officeScheduleQueries.some((query) => query.isLoading),
      isError: officeScheduleQueries.some((query) => query.isError),
    }),
    [officeScheduleQueries]
  );

  const providerSchedulesQuery = useMemo(
    () => ({
      data: isScheduleDefinitionsEnabled
        ? scheduleDefinitionQueries
            .map((query, index) => {
              const locationId = locationWiseEntityIds[index]?.locationId ?? '';
              if (!shouldShowSchedulesByLocationId[locationId]) {
                return [];
              }

              const availabilityMode = availabilityModeByLocationId[locationId] || 'PROVIDER';
              const groups =
                availabilityMode === 'OPERATORY'
                  ? (query.data?.operatoryGroups || []).map((group) => ({
                      entityId: group.operatoryId,
                      locationId,
                      scheduleDefinitions: group.scheduleDefinitions,
                    }))
                  : (query.data?.providerGroups || []).map((group) => ({
                      entityId: group.providerId,
                      locationId,
                      scheduleDefinitions: group.scheduleDefinitions,
                    }));

              return transformScheduleDefinitionGroupsToSchedules(groups, calendarDate, {
                scheduleExceptions: scheduleExceptionQueries[index]?.data?.exceptions || [],
                entityType: availabilityMode,
              });
            })
            .flat()
        : providerScheduleQueries.map((query) => query.data?.schedules || []).flat(),
      isLoading: isScheduleDefinitionsEnabled
        ? listLocationSettingsQuery.isLoading ||
          scheduleDefinitionQueries.some((query) => query.isLoading) ||
          scheduleExceptionQueries.some((query) => query.isLoading)
        : listLocationSettingsQuery.isLoading || providerScheduleQueries.some((query) => query.isLoading),
      isError: isScheduleDefinitionsEnabled
        ? listLocationSettingsQuery.isError ||
          scheduleDefinitionQueries.some((query) => query.isError) ||
          scheduleExceptionQueries.some((query) => query.isError)
        : listLocationSettingsQuery.isError || providerScheduleQueries.some((query) => query.isError),
    }),
    [
      availabilityModeByLocationId,
      calendarDate,
      isScheduleDefinitionsEnabled,
      listLocationSettingsQuery.isError,
      listLocationSettingsQuery.isLoading,
      locationWiseEntityIds,
      providerScheduleQueries,
      scheduleDefinitionQueries,
      scheduleExceptionQueries,
      shouldShowSchedulesByLocationId,
    ]
  );

  return {
    officeSchedulesQuery,
    providerSchedulesQuery,
  };
};
